import {
  PRODUCT_RESULTS_UPLOAD_CONFIG_LOADED,
  PRODUCT_RESULTS_UPLOAD_CONFIG_CLEAR,
  PRODUCT_RESULTS_INFRINGEMENTS_LOADED,
  PRODUCT_RESULTS_INFRINGEMENTS_CLEAR,
  PRODUCT_RESULTS_INFRINGEMENTS_META_LOADED,
  PRODUCT_RESULTS_INFRINGEMENTS_META_CLEAR,
  PRODUCT_RESULTS_REMOVALS_LOADED,
  PRODUCT_RESULTS_REMOVALS_CLEAR,
  PRODUCT_RESULT_DETAIL_LOADED,
  PRODUCT_RESULT_DETAIL_CLEAR,
} from '../actionTypes';
import * as api from '../apiHelper';
import { initiateRedirectRequest } from '../redirectActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../fetchLoaderActions';
import {
  arriveNotice,
  arriveCustomNotice,
} from '../noticeActions';
import { setFaviconLoading, setFaviconDefault } from '../faviconActions';


// Reducer corresponding actions
function resultsUploadConfigClear() {
  return { type: PRODUCT_RESULTS_UPLOAD_CONFIG_CLEAR };
}

function resultsUploadConfigLoaded(uploadConfig) {
  return {
    type: PRODUCT_RESULTS_UPLOAD_CONFIG_LOADED,
    payload: { uploadConfig },
  };
}

function resultsInfringementsClear() {
  return { type: PRODUCT_RESULTS_INFRINGEMENTS_CLEAR };
}

function resultsInfringementsLoaded(infringements) {
  return {
    type: PRODUCT_RESULTS_INFRINGEMENTS_LOADED,
    payload: { infringements },
  };
}

function resultsInfringementsMetaClear() {
  return { type: PRODUCT_RESULTS_INFRINGEMENTS_META_CLEAR };
}

function resultsInfringementsMetaLoaded(meta) {
  return {
    type: PRODUCT_RESULTS_INFRINGEMENTS_META_LOADED,
    payload: { meta },
  };
}

function resultsRemovalsClear() {
  return { type: PRODUCT_RESULTS_REMOVALS_CLEAR };
}

function resultsRemovalsLoaded(removals) {
  return {
    type: PRODUCT_RESULTS_REMOVALS_LOADED,
    payload: { removals },
  };
}

function resultDetailClear() {
  return { type: PRODUCT_RESULT_DETAIL_CLEAR };
}

function resultDetailLoaded(detail) {
  return {
    type: PRODUCT_RESULT_DETAIL_LOADED,
    payload: { detail },
  };
}

// API
async function getResultsUploadConfigApi(args, token) {
  const response = await api.get({
    path: 'results/bulk',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function submitResultsUploadApi(body, token, dispatch) {
  const response = await api.mixedFormDataRequest({
    path: 'results/bulk',
    body,
    token,
    version: 'v3',
    customError: true,
  });

  if (!response.ok) {
    const json = await response.json();

    if (json?.errors) {
      const customError = api.extractErrorMessage(json.errors);
      dispatch(arriveCustomNotice(customError));

      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(json.error);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(error.message));

      throw error;
    }
  }

  return response.json();
}

async function submitResultsImportApi(body, token, dispatch) {
  const response = await api.mixedFormDataRequest({
    path: 'results/imports',
    version: 'gateway',
    method: 'POST',
    token,
    body,
    customError: true,
  });

  if (!response.ok) {
    const json = await response.json();

    if (json?.errors) {
      const customError = api.extractErrorMessage(json.errors);
      dispatch(arriveCustomNotice(customError));

      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(json.error);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(error.message));

      throw error;
    }
  }

  return response.json();
}

async function submitSingleResultImportApi(body, token, dispatch) {
  const response = await api.post({
    path: 'results/imports/single',
    version: 'gateway',
    token,
    body,
    customError: true,
  });

  if (!response.ok) {
    const json = await response.json();

    if (json?.errors) {
      const customError = api.extractErrorMessage(json.errors);
      dispatch(arriveCustomNotice(customError));

      throw customError;
    } else {
      const formattedMessage = api.formatJsonError(json.error);
      const error = new Error(formattedMessage);
      dispatch(arriveNotice(error.message));

      throw error;
    }
  }

  return response.json();
}

async function getResultsInfringementsApi(args, token, isCSV) {
  if (isCSV) {
    // Remove pagination params for CSV export to get all data in one file
    const { paging, ...restArgs } = args;

    await api.getCSV({
      path: 'results/all_results',
      params: restArgs,
      token,
      version: 'v3',
      filename: 'redflag-all-infringements-export',
    });
    return null;
  }

  const response = await api.nestedParamsGet({
    path: 'results/all_results',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getResultsInfringementsMetaApi(args, token) {
  const response = await api.nestedParamsGet({
    path: 'results/results_meta',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

async function getResultDetailApi(resultId, token) {
  const response = await api.get({
    path: `results/${resultId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function getResultsRemovalsApi(args, token, isCSV) {
  if (isCSV) {
    // Remove pagination params for CSV export to get all data in one file
    const { paging, ...restArgs } = args;

    await api.getCSV({
      path: 'results/successful_removals',
      params: restArgs,
      token,
      version: 'v3',
      filename: 'redflag-successful-removals-export',
    });
    return null;
  }

  const response = await api.nestedParamsGet({
    path: 'results/successful_removals',
    params: args,
    token,
    version: 'v3',
  });
  return response.json();
}

// Helper
async function fetchResultsInfringements(dispatch, args, token, isCSV, callback = null) {
  try {
    const response = await getResultsInfringementsApi(args, token, isCSV);

    if (!isCSV) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(resultsInfringementsLoaded(response));

      if (typeof callback === 'function') {
        callback(response);
      }
    }

    if (isCSV) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
      if (isCSV) dispatch(setSubmissionLoaderReset());
    }
  }
}

async function fetchResultsInfringementsMeta(dispatch, args, token) {
  try {
    const response = await getResultsInfringementsMetaApi(args, token);

    dispatch(setFaviconDefault());
    dispatch(resultsInfringementsMetaLoaded(response));
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
    }
  }
}

async function fetchResultsRemovals(dispatch, args, token, isCSV) {
  try {
    const response = await getResultsRemovalsApi(args, token, isCSV);

    if (!isCSV) {
      dispatch(setFaviconDefault());
      dispatch(setFetchLoaderReset());
      dispatch(resultsRemovalsLoaded(response));
    }

    if (isCSV) dispatch(setSubmissionLoaderReset());
  } catch (e) {
    if (e.name !== 'AbortError') {
      dispatch(setFaviconDefault());
      dispatch(arriveNotice(e.message));
      if (isCSV) dispatch(setSubmissionLoaderReset());
    }
  }
}

// Public components actions
export function clearResultsUploadConfigRequest() {
  return (dispatch) => {
    dispatch(resultsUploadConfigClear());
  };
}

export function initiateResultsUploadConfigRequest(args) {
  return async (dispatch, getState) => {
    try {
      dispatch(resultsUploadConfigClear());

      const { auth: { token }, fetchLoader: { dataLoading } } = getState();
      if (!dataLoading) dispatch(setFetchLoaderLoading());

      const response = await getResultsUploadConfigApi(args, token);
      dispatch(resultsUploadConfigLoaded(response));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setFetchLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function submitResultsFileUploadRequest(query, rootPath, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      await submitResultsUploadApi(query, token, dispatch);

      dispatch(arriveNotice('Upload successful!', 'ok'));
      resetForm();
      dispatch(initiateRedirectRequest(null, rootPath, true));
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function submitProductResultsImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      await submitResultsImportApi(formValues, token, dispatch);

      dispatch(arriveNotice('Upload successful!', 'ok'));
      resetForm();
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function submitProductSingleResultImportRequest(formValues, resetForm) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token }, fetchLoader: { submissionLoading } } = getState();
      if (!submissionLoading) dispatch(setSubmissionLoaderLoading());

      await submitSingleResultImportApi(formValues, token, dispatch);

      dispatch(arriveNotice('Upload successful!', 'ok'));
      resetForm();
      dispatch(setSubmissionLoaderReset());
    } catch (e) {
      dispatch(setSubmissionLoaderReset());
    }
  };
}

export function clearResultsInfringementsRequest() {
  return (dispatch) => {
    dispatch(resultsInfringementsClear());
  };
}

export function initiateResultsInfringementsRequest(args, isCSV = false, callback = null) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    if (!dataLoading && !isCSV) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isCSV) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isCSV) {
      dispatch(setFaviconLoading());
    }

    await fetchResultsInfringements(dispatch, args, token, isCSV, callback);
  };
}

export function clearResultsInfringementsMetaRequest() {
  return (dispatch) => {
    dispatch(resultsInfringementsMetaClear());
  };
}

export function initiateResultsInfringementsMetaRequest(args) {
  return async (dispatch, getState) => {
    const {
      auth: { token }, favicon: { faviconStatus },
    } = getState();

    if (faviconStatus !== 'loading') {
      dispatch(setFaviconLoading());
    }

    await fetchResultsInfringementsMeta(dispatch, args, token);
  };
}

export function clearResultsRemovalsRequest() {
  return (dispatch) => {
    dispatch(resultsRemovalsClear());
  };
}

export function clearResultDetailRequest() {
  return (dispatch) => {
    dispatch(resultDetailClear());
  };
}

export function initiateResultDetailRequest(resultId) {
  return async (dispatch, getState) => {
    try {
      const { auth: { token } } = getState();
      dispatch(setFetchLoaderLoading());

      const response = await getResultDetailApi(resultId, token);
      dispatch(resultDetailLoaded(api.camelizeKeys(response)));
      dispatch(setFetchLoaderReset());
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(setFetchLoaderReset());
        dispatch(arriveNotice(e.message));
      }
    }
  };
}

export function initiateResultsRemovalsRequest(args, isCSV = false) {
  return async (dispatch, getState) => {
    const {
      auth: { token },
      favicon: { faviconStatus },
      fetchLoader: { dataLoading, submissionLoading },
    } = getState();

    if (!dataLoading && !isCSV) {
      dispatch(setFetchLoaderLoading());
    } else if (!submissionLoading && isCSV) {
      dispatch(setSubmissionLoaderLoading());
    }

    if (faviconStatus !== 'loading' && !isCSV) {
      dispatch(setFaviconLoading());
    }

    await fetchResultsRemovals(dispatch, args, token, isCSV);
  };
}
